import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';

/**
 * v0.4 章④ (§4-11b): 時刻入力の共通コンポーネント。
 * 設計: docs/design/v04-shift-labor-management-detail.md §4-11b
 *
 * ⚠️ ネイティブ `input[type=time]` と数値スピナーは `ui-guidelines.md §7.2` で禁止されているため、
 * 時・分をそれぞれ自前の `<Select>` (§8-101 対応済み) で選択する方式にする。
 * プリセット (`presets`、シフトパターン由来) を渡すと一括選択できるが、0件でも直接入力だけで
 * 完結する (§6-0 原則3: プリセットが無いテナントを分岐にしない)。
 *
 * ⚠️ 内部表現は常に 'HH:MM' の開始〜終了時刻 (§4-2b)。'26:30' 形式は使わない。
 * crossesMidnight は表示用の補助情報として自動算出するのみで、呼び出し側の値には含めない
 * (shift_preferences 等 crosses_midnight 列を持たないテーブルもあるため、永続化するかは呼び出し側の判断)。
 */

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'];

export type TimeRangePreset = {
  id: string;
  label: string;
  startTime: string;
  endTime: string;
  /** 2026-09-09: 呼び出し側が任意で休憩時間を自動反映するために使う。TimeRangeInput自体は
   *  休憩欄を持たないため参照しない (呼び出し側がonPresetSelectで自分のpresets配列から引く)。 */
  breakMinutes?: number;
};

export type TimeRangeInputValue = {
  startTime: string | null;
  endTime: string | null;
};

type TimeSelectProps = {
  label: string;
  value: string | null;
  onChange: (next: string) => void;
  disabled?: boolean;
};

export function TimeSelect({ label, value, onChange, disabled }: TimeSelectProps) {
  const [h, m] = value ? value.split(':') : [null, null];
  return (
    <div className="flex items-center gap-1">
      <Select value={h ?? undefined} onValueChange={(nh) => onChange(`${nh}:${m ?? '00'}`)} disabled={disabled}>
        <SelectTrigger className="w-[68px] min-h-[44px]" aria-label={`${label}(時)`}>
          <SelectValue placeholder="--" />
        </SelectTrigger>
        <SelectContent>
          {HOURS.map((hh) => <SelectItem key={hh} value={hh}>{hh}</SelectItem>)}
        </SelectContent>
      </Select>
      <span className="text-text-sub" aria-hidden>:</span>
      <Select value={m ?? undefined} onValueChange={(nm) => onChange(`${h ?? '00'}:${nm}`)} disabled={disabled}>
        <SelectTrigger className="w-[68px] min-h-[44px]" aria-label={`${label}(分)`}>
          <SelectValue placeholder="--" />
        </SelectTrigger>
        <SelectContent>
          {MINUTES.map((mm) => <SelectItem key={mm} value={mm}>{mm}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export type TimeRangeInputProps = {
  value: TimeRangeInputValue;
  onChange: (next: TimeRangeInputValue) => void;
  /** true の間「終日」チップを表示する。選択時は start/end を両方 null にする (nullable=終日 のテーブル向け) */
  allowAllDay?: boolean;
  /** シフトパターンからの一括選択。0件/未指定ならプリセットUIごと非表示になる (§6-0 原則3) */
  presets?: TimeRangePreset[];
  /** v0.4 追加7要件⑤ (D-235): どのプリセットが選択されているかを制御表示する (由来記録の永続化に必要)。
   *  未指定なら非制御 (選択後もプレースホルダ表示に戻る、従来の挙動)。 */
  selectedPresetId?: string | null;
  /** プリセットが選択された時、onChange と同時に呼ばれる (どのパターンを選んだかの記録用)。 */
  onPresetSelect?: (presetId: string) => void;
  /** v0.4 追加7要件⑤ (D-235): true かつ presets が1件以上あれば、手動の時・分選択を非表示にし
   *  プリセットからの選択のみを許可する (「パターン選択のみ」入力モード用)。presets が0件のときは
   *  無視され常に手動入力を表示する (§6-0 原則3、データの不在によるフォールバック)。 */
  hideManualInput?: boolean;
  disabled?: boolean;
  /** 入力欄グループの見出し (省略可) */
  legend?: string;
};

/** ⚠️ design-expert H-1 指摘: 「終了 <= 開始」を翌日跨ぎと判定する (`<` だと開始=終了(24時間勤務)の
 *  場合に跨ぎ判定が漏れ、表示と保存側の実態が乖離する)。呼び出し元 (ShiftBoardPage の保存ロジック等)
 *  と必ず同じ式を使うこと — v0.4 F-3 で4箇所に式が重複していたため、ここへ集約した (§6 基準)。 */
export const computesCrossesMidnight = (value: TimeRangeInputValue): boolean =>
  !!(value.startTime && value.endTime && value.endTime <= value.startTime);

export function TimeRangeInput({
  value, onChange, allowAllDay, presets, selectedPresetId, onPresetSelect, hideManualInput, disabled, legend,
}: TimeRangeInputProps) {
  const isAllDay = !!allowAllDay && value.startTime === null && value.endTime === null;
  const crossesMidnight = computesCrossesMidnight(value);
  const showManualInput = !hideManualInput || !presets || presets.length === 0;

  const applyPreset = (presetId: string) => {
    const preset = presets?.find((p) => p.id === presetId);
    if (preset) { onChange({ startTime: preset.startTime, endTime: preset.endTime }); onPresetSelect?.(presetId); }
  };

  return (
    <div className="space-y-2">
      {legend && <div className="text-xs font-bold text-text-sub">{legend}</div>}
      <div className="flex flex-wrap items-center gap-2">
        {allowAllDay && (
          <button
            type="button"
            aria-pressed={isAllDay}
            disabled={disabled}
            onClick={() => onChange(isAllDay ? { startTime: '09:00', endTime: '17:00' } : { startTime: null, endTime: null })}
            className={`min-h-[44px] px-3 rounded-full text-xs border transition disabled:opacity-45 ${
              isAllDay
                ? 'bg-misespark-navy text-white border-misespark-navy'
                : 'border-surface-border text-text-body hover:bg-surface-sunken'
            }`}
          >
            終日
          </button>
        )}
        {/* ⚠️ 終日のときも出す。終日は「営業時間に合わせたい」場面そのものであり、
            ここで隠すとプリセットへ辿り着くのに一度終日を解除する必要があった (design-and-code 指摘)。
            プリセットを選べば start/end が入り自動的に終日から抜ける。 */}
        {presets && presets.length > 0 && (
          <Select value={selectedPresetId ?? undefined} onValueChange={applyPreset} disabled={disabled}>
            <SelectTrigger className="w-[168px] min-h-[44px]"><SelectValue placeholder="パターンから選ぶ" /></SelectTrigger>
            <SelectContent>
              {presets.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.label} ({p.startTime}〜{p.endTime})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {!isAllDay && showManualInput && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <TimeSelect label="開始時刻" value={value.startTime} onChange={(v) => onChange({ ...value, startTime: v })} disabled={disabled} />
            <span className="text-text-sub" aria-hidden>〜</span>
            <TimeSelect label="終了時刻" value={value.endTime} onChange={(v) => onChange({ ...value, endTime: v })} disabled={disabled} />
          </div>
        )}
        {/* design-expert HIGH-1 (v0.4 追加7要件⑤): hideManualInput=true (パターン選択のみモード) でも
            翌日跨ぎの情報は表示する。手動入力欄の内側に置くと非表示になってしまうため独立させる。 */}
        {!isAllDay && crossesMidnight && (
          <span className="text-xs text-text-sub px-1.5 py-0.5 rounded bg-surface-sunken shrink-0">翌日</span>
        )}
      </div>
    </div>
  );
}
